<script setup>
import { LogOut, Monitor } from 'lucide-vue-next';

import AuthenticationButtons from '@/components/authentication/AuthenticationButtons';
import HeaderAccountInfo from '@/components/header/HeaderAccountInfo.vue';
import MobileNavItems from '@/components/header/MobileNavItems.vue';
import Switcher from '@/components/ui/inputs/Switcher';
import Logo from '@/components/ui/logo/Logo';
import { useConnectedChannels } from '@/composables/useConnectedChannels';
import { useIsTouchDevice } from '@/composables/useIsTouchDevice';
import { useNavItemVisibility } from '@/composables/useNavItemVisibility';
import navItems from '@/navigation/vertical';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { useViewportStore } from '@/stores/viewport';

const emit = defineEmits(['hideMenu']);
const themeStore = useThemeStore();
const authStore = useAuthStore();
const viewportStore = useViewportStore();
const { isTouchDevice } = useIsTouchDevice();

const hideMenu = () => {
  emit('hideMenu');
};

// Close the menu before switching: in desktop viewport the burger block gets display:none,
// which would strand the open overlay and the body scroll-lock class.
const setViewportPreference = (enabled) => {
  hideMenu();
  viewportStore.setPreference(enabled ? 'desktop' : 'mobile');
};

const { isEnvAllowed } = useNavItemVisibility();
useConnectedChannels({ isEnvAllowed }).startLoading();
</script>

<template>
  <main class="main">
    <div class="content__header">
      <RouterLink :to="{ name: 'dashboard' }" @click="hideMenu">
        <Logo />
      </RouterLink>
      <HeaderAccountInfo v-if="authStore.loggedIn" />
      <div class="mobile-nav">
        <Switcher
          :default-value="themeStore.userTheme === themeStore.LIGHT_THEME"
          :themes="[{ name: 'light' }, { name: 'dark' }]"
          @change="themeStore.toggleTheme()"
        />
        <button
          class="close"
          @click="hideMenu"
        >
          <VIcon icon="ic:twotone-close" />
        </button>
      </div>
    </div>

    <div class="content">
      <div class="content__body">
        <div
          v-if="isTouchDevice"
          class="mobile-nav-list__viewport-toggle"
        >
          <Monitor
            :size="20"
            class="mobile-nav-list__icon"
          />
          {{ $t('Desktop view') }}
          <ElSwitch
            :model-value="viewportStore.isDesktopViewport"
            @change="setViewportPreference"
          />
        </div>

        <div v-if="authStore.loggedIn">
          <nav class="mobile-nav-list">
            <MobileNavItems
              :items="navItems"
              @navigate="hideMenu"
            />
          </nav>
        </div>
        <AuthenticationButtons
          v-else
          class="header-authentication"
          login-button-text="Log In"
          register-button-text="Sign Up"
          @navigate="hideMenu"
        />

        <button
          v-if="authStore.loggedIn"
          class="mobile-nav-list__logout"
          @click="authStore.logout()"
        >
          <LogOut :size="20" />
          {{ $t('Log Out') }}
        </button>
      </div>

      <footer />
    </div>
  </main>
</template>

<style lang="scss" scoped>
main {
  overflow-y: scroll;
  min-height: 100vh;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 56px);
  padding: 2px 16px 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 10px;
    background-color: var(--wp-surface-default);

    .close {
      display: flex;
      background-color: transparent;
    }
  }

  &__body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding-bottom: 64px;
  }
}

.settings__select {
  height: 40px;
  display: none;
  &:deep(.select__main-btn) {
    background: var(--wp-surface-secondary);
    display: flex;
    align-content: center;
    justify-content: center;
    padding: 4px;
    border-radius: 18px;
    transition: none;
  }
}

@include laptop {
  .settings__select {
    display: block;
  }
}

.gift {
  position: relative;
  z-index: 1;
  padding: 16px 0;

  &:after {
    content: '';
    display: block;
    position: absolute;
    z-index: -1;
    top: 0;
    right: -16px;
    bottom: 0;
    left: -16px;
    background-color: var(--wp-surface-secondary);
  }

  a {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    text-decoration: unset;
    color: var(--wp-text-primary);
    gap: var(--gap-small);
  }

  &::v-deep(mark) {
    background-color: transparent;
  }

  mark {
    color: var(--wp-text-secondary);
  }
}

.mobile-nav-list {
  margin-top: 8px;

  &__icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    opacity: 0.75;
  }

  &__viewport-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 0;
    font-size: 1.1rem;
    font-weight: 500;
    color: var(--wp-text-primary);
    border-bottom: 1px solid var(--wp-border-primary);

    .el-switch {
      margin-left: auto;
    }
  }

  &__logout {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 16px 0;
    font-size: 1.1rem;
    font-weight: 500;
    color: var(--wp-text-primary);
    background: transparent;
    border: none;
    cursor: pointer;
  }
}

.list {
  margin-bottom: 80px;
  font-size: 1.25rem;

  &__item {
    border-bottom: 1px solid var(--wp-border-primary);

    &.theme {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__link {
    display: flex;
    position: relative;
    align-items: center;
    width: 100%;
    padding: 25px 0;
    text-decoration: unset;
    color: var(--wp-text-primary);
    gap: var(--gap-small);

    &.btn {
      width: 100%;
      background-color: transparent;
    }
  }
}

.chevron {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.portfolio-content {
  width: 100%;
}

.settings {
  display: flex;
  flex-wrap: nowrap;
  padding: 16px 0;
  gap: var(--gap-small);

  .theme-button {
    flex-shrink: 0;
  }

  &__select {
    flex: 1 1 auto;
  }
}

.info {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--gap-small);

  &__link {
    font-size: 0.875rem;
    text-decoration: none;
    color: var(--wp-text-tertiary);
  }
}

.menu {
  width: 80%;
  margin-left: auto;
}

.mobile-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.menu-user {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  &-info {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
  }

  &-name {
    font-size: 1.25rem;
    line-height: 1.5rem;
    text-transform: capitalize;
    color: var(--wp-text-primary);
  }

  &-status {
    letter-spacing: 0.02em;
  }

  &-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: var(--wp-surface-bg-icon);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 16px;
      height: 16px;
    }

    &:deep(path) {
      fill: var(--wp-icon-accent);
    }
  }

  &-logout {
    margin-bottom: 16px;
  }
}

:deep(.logo) {
  width: 50px;
  height: 40px;
}

:deep(.mobile-logo) {
  display: block !important;
  width: 50px;
  height: 40px;
}

:deep(.desktop-logo) {
  display: none !important;
}

@include laptop {
  :deep(.header.layout-default-header .mobile-logo) {
    display: none;
  }

  :deep(.header.layout-default-header .desktop-logo) {
    display: block;
  }
}
</style>
